import React from 'react';

const EarthLoader = ({ message = "Loading..." }) => {
  return (
    <div className="monet-wrapper">
      <style>{`
        .monet-wrapper {
          display: flex;
          justify-content: center;
          align-items: center;
          padding: 3rem;
          font-family: 'Georgia', serif;
          width: 100%;
          height: 100%;
        }
        
        .earth-container {
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        /* The circular frame */
        .earth-loader {
          width: 8em;
          height: 8em;
          position: relative;
          overflow: hidden;
          border-radius: 50%;
          
          /* Monet Water Colors: A blend of deep blues, teals, and light purples */
          background: radial-gradient(circle at 30% 30%, 
            #8faab3 0%,    /* Light hazy blue */
            #5d7ea3 40%,   /* Water blue */
            #2c406e 100%   /* Deep shadow blue */
          );
          
          /* Soft, glowing border instead of hard line */
          box-shadow: 
            inset 0 0 20px rgba(0,0,0,0.3), /* Inner depth */
            0 0 15px rgba(93, 126, 163, 0.4); /* Outer glow */
            
          border: 3px solid rgba(255, 255, 255, 0.6);
          outline: 2px solid rgba(93, 126, 163, 0.3);
        }

        /* Simulating Canvas Texture */
        .texture-overlay {
          position: absolute;
          top: 0; left: 0; right: 0; bottom: 0;
          z-index: 10;
          pointer-events: none;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)' opacity='0.15'/%3E%3C/svg%3E");
          mix-blend-mode: overlay;
        }

        .loading-text {
          color: #654321;
          margin-top: 1.5em;
          font-size: 1.25em;
          font-style: italic;
          font-weight: 600;
          letter-spacing: 0.05em;
          text-shadow: 2px 2px 4px rgba(160, 160, 160, 0.3);
        }

        /* Continents Styling */
        .continent {
          position: absolute;
          width: 8em;
          height: auto;
          
          /* The Magic: Apply the Oil Paint Filter */
          filter: url(#oil-paint);
          
          /* Mix blend mode helps the "paint" sit on the "water" naturally */
          mix-blend-mode: multiply;
        }

        /* Monet Land Colors: Olive, Ochre, and pale greens */
        .continent path {
          fill: #6d7f42; /* Base Olive */
          stroke: #d6cf8e; /* Impressionist highlight strokes */
          stroke-width: 2px;
          stroke-opacity: 0.5;
        }
        
        /* Varying colors for different continents for artistic variety */
        .c1 path { fill: #556b2f; stroke: #8fbc8f; } /* Dark Olive & Sea Green */
        .c2 path { fill: #808000; stroke: #f0e68c; } /* Olive & Khaki */
        .c3 path { fill: #6b8e23; stroke: #9acd32; } /* Olive Drab & Yellow Green */
        .c4 path { fill: #556b2f; stroke: #8fbc8f; }

        /* Animation Logic */
        .c1 {
          bottom: -2.5em;
          animation: rotateGlobe 12s infinite linear;
        }
        .c2 {
          top: -3.5em;
          animation: rotateGlobe 12s infinite linear -4s;
        }
        .c3 {
          top: -2.5em;
          animation: rotateGlobe 12s infinite linear -8s;
        }
        .c4 {
          bottom: -2.5em;
          animation: rotateGlobe 12s infinite linear -6s; /* Offset slightly */
          opacity: 0.8;
        }

        /* Smoother, dreamier animation. Monet's water lilies don't zip around; they drift. */
        @keyframes rotateGlobe {
          0% {
            left: -5em;
            transform: translateX(0) scale(0.9) skewX(5deg);
            opacity: 0;
          }
          10% {
            opacity: 0.8;
          }
          20% {
            opacity: 1;
          }
          50% {
            transform: translateX(6em) scale(1.1) skewX(-5deg); /* Moves to center, grows slightly */
          }
          80% {
            opacity: 1;
          }
          90% {
            opacity: 0.8;
          }
          100% {
            left: 10em; /* Moves out of frame */
            transform: translateX(0) scale(0.9) skewX(5deg);
            opacity: 0;
          }
        }
      `}</style>

      <div className="earth-container">
        {/* SVG Filter Definitions to create the oil painting effect */}
        <svg style={{ position: 'absolute', width: 0, height: 0 }}>
          <defs>
            <filter id="oil-paint">
              {/* Turbulence creates a noise texture (canvas grain) */}
              <feTurbulence type="fractalNoise" baseFrequency="0.015" numOctaves="3" result="noise" />
              
              {/* DisplacementMap distorts the source image using the noise, creating "brush strokes" */}
              <feDisplacementMap in="SourceGraphic" in2="noise" scale="6" xChannelSelector="R" yChannelSelector="G" result="distorted" />
              
              {/* GaussianBlur softens the edges to look like wet paint */}
              <feGaussianBlur in="distorted" stdDeviation="0.8" result="blurred" />
              
              {/* Lighting effect to give it depth (impasto technique) */}
              <feSpecularLighting in="blurred" surfaceScale="2" specularConstant="0.8" specularExponent="20" lightingColor="#fff" result="specular">
                <fePointLight x="-5000" y="-10000" z="20000" />
              </feSpecularLighting>
              
              <feComposite in="specular" in2="blurred" operator="in" result="specular" />
              <feComposite in="specular" in2="blurred" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" />
            </filter>
            
            <filter id="water-shimmer">
               <feTurbulence type="turbulence" baseFrequency="0.05" numOctaves="2" result="turbulence" />
               <feDisplacementMap in2="turbulence" in="SourceGraphic" scale="3" xChannelSelector="R" yChannelSelector="G" />
            </filter>
          </defs>
        </svg>

        <div className="earth-frame">
          <div className="earth-loader">
            <div className="texture-overlay"></div>
            
            {/* We apply the oil-paint filter to the continents */}
            <div className="continents-group">
                <svg className="continent c1" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                    <path transform="translate(100 100)" d="M29.4,-17.4C33.1,1.8,27.6,16.1,11.5,31.6C-4.7,47,-31.5,63.6,-43,56C-54.5,48.4,-50.7,16.6,-41,-10.9C-31.3,-38.4,-15.6,-61.5,-1.4,-61C12.8,-60.5,25.7,-36.5,29.4,-17.4Z" />
                </svg>
                <svg className="continent c2" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                    <path transform="translate(100 100)" d="M31.7,-55.8C40.3,-50,45.9,-39.9,49.7,-29.8C53.5,-19.8,55.5,-9.9,53.1,-1.4C50.6,7.1,43.6,14.1,41.8,27.6C40.1,41.1,43.4,61.1,37.3,67C31.2,72.9,15.6,64.8,1.5,62.2C-12.5,59.5,-25,62.3,-31.8,56.7C-38.5,51.1,-39.4,37.2,-49.3,26.3C-59.1,15.5,-78,7.7,-77.6,0.2C-77.2,-7.2,-57.4,-14.5,-49.3,-28.4C-41.2,-42.4,-44.7,-63,-38.5,-70.1C-32.2,-77.2,-16.1,-70.8,-2.3,-66.9C11.6,-63,23.1,-61.5,31.7,-55.8Z" />
                </svg>
                <svg className="continent c3" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                    <path transform="translate(100 100)" d="M30.6,-49.2C42.5,-46.1,57.1,-43.7,67.6,-35.7C78.1,-27.6,84.6,-13.8,80.3,-2.4C76.1,8.9,61.2,17.8,52.5,29.1C43.8,40.3,41.4,53.9,33.7,64C26,74.1,13,80.6,2.2,76.9C-8.6,73.1,-17.3,59,-30.6,52.1C-43.9,45.3,-61.9,45.7,-74.1,38.2C-86.4,30.7,-92.9,15.4,-88.6,2.5C-84.4,-10.5,-69.4,-20.9,-60.7,-34.6C-52.1,-48.3,-49.8,-65.3,-40.7,-70C-31.6,-74.8,-15.8,-67.4,-3.2,-61.8C9.3,-56.1,18.6,-52.3,30.6,-49.2Z" />
                </svg>
                <svg className="continent c4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 200 200">
                    <path transform="translate(100 100)" d="M39.4,-66C48.6,-62.9,51.9,-47.4,52.9,-34.3C53.8,-21.3,52.4,-10.6,54.4,1.1C56.3,12.9,61.7,25.8,57.5,33.2C53.2,40.5,39.3,42.3,28.2,46C17,49.6,8.5,55.1,1.3,52.8C-5.9,50.5,-11.7,40.5,-23.6,37.2C-35.4,34,-53.3,37.5,-62,32.4C-70.7,27.4,-70.4,13.7,-72.4,-1.1C-74.3,-15.9,-78.6,-31.9,-73.3,-43C-68.1,-54.2,-53.3,-60.5,-39.5,-60.9C-25.7,-61.4,-12.9,-56,1.1,-58C15.1,-59.9,30.2,-69.2,39.4,-66Z" />
                </svg>
            </div>
          </div>
        </div>
        <p className="loading-text">{message}</p>
      </div>
    </div>
  );
}

export default EarthLoader;